import type { FetchNextPageOptions } from '@tanstack/react-query';
import debounce from 'debounce';
import { useRef, useState, useEffect } from 'react';
import type { DataAttributes } from 'shared/lib/data-auto-test';
import type { MenuInfiniteProps } from 'shared/ui/MultiSelect/Components';
import { MenuBaseComponent, MenuInfinite, MenuSearchComponent } from 'shared/ui/MultiSelect/Components';
import type { MultiSelectProps, Option } from 'shared/ui/MultiSelect/interfaces';
import { SelectButton } from 'shared/ui/MultiSelect/Components/SelectButton';

import { MultiSelectComponent } from './MultiSelect';
import { isOptionsWithFilterKey, mapFiltersFromUrlToOptions, parseFromToFilters, useUrlParamCommands } from './utils';

const SET_FILTERS_TIMEOUT = 300;

interface MultiSelectQueriesCommonProps extends Omit<MultiSelectProps, 'setSelected' | 'selectedOptions'> {
  filterName?: string;
  rootClassName: string;
  loading?: boolean;
  testId?: string;
  attributes?: DataAttributes['attributes'];
  isFromTo?: boolean;
  initialSelectedOptions?: Option[];
  onUpdate?: () => void;
  refreshToken?: string | string[] | null;
  withResize?: boolean;
  isWhite?: boolean;
  isHighlighted?: boolean;
}

export function MultiSelectWithQueriesUrl(props: MultiSelectQueriesCommonProps) {
  const { initialSelectedOptions, onUpdate } = props;

  const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);
  const { updateFilterKeyURLParamCommand, updateFromToURLParamCommand, updateURLParamCommand } = useUrlParamCommands();

  const handleResetOptionsFromUrl = () => {
    const queryString = window.location.search;

    const allParams = new URLSearchParams(queryString);

    if (props.filterName && props.isFromTo) {
      const filters = parseFromToFilters(allParams, props.filterName);
      const matchedOptions = mapFiltersFromUrlToOptions(props.options, filters);

      setSelectedOptions(matchedOptions);

      return;
    }

    if (props.filterName) {
      const selectedIdsFromUrl = allParams.getAll(props.filterName);

      if (selectedIdsFromUrl.length) {
        const optionsFromUrl = props.options.filter((option) => selectedIdsFromUrl.includes(option.id));

        setSelectedOptions(optionsFromUrl);
      }

      if (selectedIdsFromUrl.length === 0) {
        setSelectedOptions([]);
      }

      return;
    }

    if (isOptionsWithFilterKey(props.options)) {
      const optionsKeys = props.options.map((option) => option.filterKey);
      const selectedIdsFromUrl = optionsKeys.filter((key) => !!allParams.get(key));

      if (selectedIdsFromUrl.length) {
        const optionsFromUrl = props.options.filter((option) => selectedIdsFromUrl.includes(option.filterKey));

        setSelectedOptions(optionsFromUrl);
      }

      if (selectedIdsFromUrl.length === 0) {
        setSelectedOptions([]);
      }
    }
  };

  useEffect(() => {
    window.addEventListener('popstate', handleResetOptionsFromUrl);

    return () => {
      window.removeEventListener('popstate', handleResetOptionsFromUrl);
    };
  }, [JSON.stringify(props.options)]);

  useEffect(() => {
    handleResetOptionsFromUrl();
  }, [props.refreshToken]);

  useEffect(() => {
    if (initialSelectedOptions?.length) {
      setSelectedOptions((prevOptions) => {
        const prevOptionsMap = prevOptions.reduce(
          (acc, option) => ({
            ...acc,
            [option.id]: option.id,
          }),
          {} as Record<string, string>
        );

        const options = initialSelectedOptions.filter((option) => !prevOptionsMap[option.id]);

        return [...prevOptions, ...options];
      });
    }
  }, [initialSelectedOptions]);

  const debounceRef = useRef(
    debounce((items: Option[], selectedPervOptions: Option[]) => {
      onUpdate && onUpdate();
      if (props.filterName && props.isFromTo) {
        updateFromToURLParamCommand(items, selectedPervOptions, props.filterName);

        return;
      }

      if (props.filterName) {
        updateURLParamCommand(items, selectedPervOptions, props.filterName);

        return;
      }

      if (isOptionsWithFilterKey(props.options)) {
        updateFilterKeyURLParamCommand(props.options, items);
      }
    }, SET_FILTERS_TIMEOUT)
  );

  useEffect(() => {
    const queryString = window.location.search;
    const ignoreDataFromUrl = !queryString || selectedOptions.length || initialSelectedOptions?.length;

    if (ignoreDataFromUrl) {
      return;
    }

    const allParams = new URLSearchParams(queryString);

    if (props.filterName && props.isFromTo) {
      const filters = parseFromToFilters(allParams, props.filterName);
      const matchedOptions = mapFiltersFromUrlToOptions(props.options, filters);

      setSelectedOptions(matchedOptions);

      return;
    }

    if (props.filterName) {
      const selectedIdsFromUrl = allParams.getAll(props.filterName);

      if (selectedIdsFromUrl.length) {
        const optionsFromUrl = props.options.filter((option) => selectedIdsFromUrl.includes(option.id));

        setSelectedOptions(optionsFromUrl);
      }

      return;
    }

    if (isOptionsWithFilterKey(props.options)) {
      const optionsKeys = props.options.map((option) => option.filterKey);
      const selectedIdsFromUrl = optionsKeys.filter((key) => !!allParams.get(key));

      if (selectedIdsFromUrl.length) {
        const optionsFromUrl = props.options.filter((option) => selectedIdsFromUrl.includes(option.filterKey));

        setSelectedOptions(optionsFromUrl);
      }

      if (selectedIdsFromUrl.length === 0) {
        setSelectedOptions([]);
      }
    }
  }, [props.options]);

  const handleSelected = (items: Option[]) => {
    setSelectedOptions(items);
    debounceRef.current(items, selectedOptions);
  };

  return (
    <div
      data-testid={props.title}
      className={props.rootClassName}
      {...props.attributes}
    >
      <MultiSelectComponent
        {...props}
        size={props.size ?? 'small'}
        setSelected={handleSelected}
        selectedOptions={selectedOptions}
        key={`${props.title}_${props.loading}`}
      />
    </div>
  );
}

function Search(props: Omit<MultiSelectQueriesCommonProps, 'renderCustomMenu'>) {
  return (
    <MultiSelectWithQueriesUrl
      {...props}
      renderCustomMenu={MenuSearchComponent}
    />
  );
}

function SearchWithoutSelectAll(props: Omit<MultiSelectQueriesCommonProps, 'renderCustomMenu'>) {
  return (
    <MultiSelectWithQueriesUrl
      {...props}
      renderCustomMenu={(props) => MenuSearchComponent({ ...props, isSelectAll: false })}
    />
  );
}

function MenuList(props: Omit<MultiSelectQueriesCommonProps, 'renderCustomMenu'>) {
  return (
    <MultiSelectWithQueriesUrl
      {...props}
      renderCustomMenu={(props) => MenuBaseComponent({ ...props, isSelectAll: false })}
    />
  );
}

interface SelectProps extends Omit<MultiSelectQueriesCommonProps, 'renderCustomMenu'> {
  withoutCheckbox?: boolean;
  singleSelect?: boolean;
  withCancel?: boolean;
  leftIcon?: string;
}

function Select(props: SelectProps) {
  return (
    <MultiSelectWithQueriesUrl
      {...props}
      renderCustomButton={(btnProps) => (
        <SelectButton
          {...btnProps}
          setSelected={(option) => {
            if (option) {
              btnProps.setSelected([option]);
            } else {
              btnProps.setSelected([]);
            }
          }}
          selectedOption={btnProps.selectedOptions[0]}
          withCancel={props.withCancel ?? true}
          isWhite={props.isWhite}
          leftIcon={props.leftIcon}
          isHighlighted={props.isHighlighted}
        />
      )}
      renderCustomMenu={(menuProps) => (
        <MenuSearchComponent
          {...menuProps}
          isSelectAll={false}
          singleSelect
          withoutCheckbox
        />
      )}
    />
  );
}

interface InfinityListProps
  extends Omit<MultiSelectQueriesCommonProps, 'renderCustomMenu'>,
    Pick<MenuInfiniteProps<Option>, 'updateOptionsHandler'> {
  hasNextPage: boolean;
  loading: boolean;
  totalItems: number;
  totalItemsWithoutFilters: number;
  isFetchingNextPage: boolean;
  isFetching: boolean;
  removeQuery: () => void;
  onChangeSearchQuery: (value: string) => void;
  fetchNextPage: (params?: FetchNextPageOptions) => void;
  withoutCheckbox?: boolean;
  singleSelect?: boolean;
  menuSearchPlaceholder?: string;
  /** removes text clip with tooltip if set to true */
  showFullText?: boolean;
}

function InfinityList(props: InfinityListProps) {
  const {
    hasNextPage,
    loading,
    isFetchingNextPage,
    isFetching,
    totalItems,
    totalItemsWithoutFilters,
    fetchNextPage,
    onChangeSearchQuery,
    removeQuery,
    filterName,
    initialSelectedOptions = [],
    withoutCheckbox,
    singleSelect,
    updateOptionsHandler,
    menuSearchPlaceholder,
    showFullText,
    ...restProps
  } = props;

  const totalListItems = totalItems < totalItemsWithoutFilters ? totalItems : totalItemsWithoutFilters;

  return (
    <MultiSelectWithQueriesUrl
      {...restProps}
      filterName={filterName}
      initialSelectedOptions={initialSelectedOptions}
      renderCustomButton={
        singleSelect
          ? (btnProps) => (
              <SelectButton
                {...btnProps}
                setSelected={(option) => {
                  if (option) {
                    btnProps.setSelected([option]);
                  } else {
                    btnProps.setSelected([]);
                  }
                }}
                selectedOption={btnProps.selectedOptions[0]}
                withCancel
              />
            )
          : undefined
      }
      renderCustomMenu={(props) => (
        <MenuInfinite
          {...props}
          withResize={props.withResize}
          handleClose={props.handleClose}
          hasNextPage={hasNextPage}
          loading={loading}
          isFetchingNextPage={isFetchingNextPage}
          isFetching={isFetching}
          fetchNextPage={fetchNextPage}
          totalItems={totalListItems}
          removeQuery={removeQuery}
          withoutCheckbox={withoutCheckbox}
          singleSelect={singleSelect}
          updateOptionsHandler={updateOptionsHandler}
          onChangeSearchQuery={onChangeSearchQuery}
          placeholder={menuSearchPlaceholder}
          showFullText={showFullText}
        />
      )}
    />
  );
}

MultiSelectWithQueriesUrl.Search = Search;
MultiSelectWithQueriesUrl.SearchWithoutSelectAll = SearchWithoutSelectAll;
MultiSelectWithQueriesUrl.MenuList = MenuList;
MultiSelectWithQueriesUrl.InfinityList = InfinityList;
MultiSelectWithQueriesUrl.Select = Select;
